import { useState, type ChangeEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Icon } from './Icon.tsx';
import { manualQuoteApi, type ManualDoc } from '../features/manualQuote/api.ts';
import { comparisonApi, type PoUpload } from '../features/comparison/api.ts';
import { vendorsApi } from '../features/vendors/api.ts';
import { downloadPdf } from '../features/documents/api.ts';
import { fileToBase64, UPLOAD_ACCEPT } from '../lib/upload.ts';
import { useAuthStore } from '../store/auth.ts';
import { canProcure } from '../lib/roles.ts';
import { t } from '../lib/strings.ts';

interface Props {
  requestId: string;
}

function fmtMvr(n: number): string {
  return `MVR ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// A single upload box (label + add button + file list). Read-only when `locked`.
function DocBox({
  label,
  hint,
  docs,
  uploading,
  onPick,
  onRemove,
  removing,
  locked,
}: {
  label: string;
  hint?: string;
  docs: { id: string; fileName: string }[];
  uploading: boolean;
  onPick: (e: ChangeEvent<HTMLInputElement>) => void;
  onRemove?: (id: string) => void;
  removing?: boolean;
  locked?: boolean;
}): JSX.Element {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="status-label text-muted-foreground">{label}</span>
        {!locked && (
          <label className="btn-secondary btn-sm cursor-pointer gap-1">
            <Icon name="upload" size={14} />
            {uploading ? t.manualQuote.uploading : t.manualQuote.addFile}
            <input type="file" multiple accept={UPLOAD_ACCEPT} className="hidden" onChange={(e) => void onPick(e)} disabled={uploading} />
          </label>
        )}
      </div>
      {docs.length > 0 ? (
        <ul className="mt-2 divide-y divide-border text-sm">
          {docs.map((d) => (
            <li key={d.id} className="flex items-center justify-between gap-2 py-1">
              <span className="flex min-w-0 items-center gap-1.5">
                <Icon name="description" size={14} className="shrink-0 text-muted-foreground" />
                <span className="truncate">{d.fileName}</span>
              </span>
              {!locked && onRemove && (
                <button type="button" onClick={() => onRemove(d.id)} disabled={removing} className="btn-ghost btn-sm text-destructive" aria-label={t.manualQuote.remove}>
                  <Icon name="close" size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      ) : hint ? (
        <p className="mt-2 text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}

export function ManualQuotePanel({ requestId }: Props): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  const canAct = canProcure(role);
  const qc = useQueryClient();

  const [vendorId, setVendorId] = useState('');
  const [amount, setAmount] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const { data: state, isLoading } = useQuery({
    queryKey: ['manualState', requestId],
    queryFn: () => manualQuoteApi.get(requestId),
  });

  const { data: vendorData } = useQuery({
    queryKey: ['vendors', 'options'],
    queryFn: () => vendorsApi.list({ pageSize: 200 }),
    enabled: canAct && !state?.award,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['manualState', requestId] });
    void qc.invalidateQueries({ queryKey: ['request', requestId] });
    void qc.invalidateQueries({ queryKey: ['poUploads', requestId] });
  };

  const uploadMut = useMutation({
    mutationFn: async ({ kind, file }: { kind: 'QUOTATION' | 'INVOICE'; file: File }) => {
      const { dataBase64, mime } = await fileToBase64(file);
      return manualQuoteApi.uploadDoc(requestId, { kind, fileName: file.name, mime, dataBase64 });
    },
    onSuccess: invalidate,
    onError: () => setErr(t.manualQuote.uploadFailed),
  });

  const deleteMut = useMutation({
    mutationFn: (docId: string) => manualQuoteApi.deleteDoc(requestId, docId),
    onSuccess: invalidate,
    onError: () => setErr(t.manualQuote.deleteFailed),
  });

  const awardMut = useMutation({
    mutationFn: () => manualQuoteApi.award(requestId, vendorId, Number(amount)),
    onSuccess: invalidate,
    onError: () => setErr(t.manualQuote.awardFailed),
  });

  const sendMut = useMutation({
    mutationFn: () => manualQuoteApi.sendToFinance(requestId),
    onSuccess: invalidate,
    onError: () => setErr(t.manualQuote.sendFailed),
  });

  // "Other docs" attached at the send-for-payment step reuse the PO-upload store
  // (kind OTHER), which is editable while the award hasn't been sent to Finance.
  const otherEnabled = Boolean(state?.award) && !state?.locked;
  const { data: poDocs } = useQuery({
    queryKey: ['poUploads', requestId],
    queryFn: () => comparisonApi.listPoUploads(requestId),
    enabled: otherEnabled && canAct,
  });
  const others = (poDocs ?? []).filter((d: PoUpload) => d.kind === 'OTHER');

  const otherUploadMut = useMutation({
    mutationFn: async (file: File) => {
      const { dataBase64, mime } = await fileToBase64(file);
      return comparisonApi.uploadPo(requestId, { kind: 'OTHER', fileName: file.name, mime, dataBase64 });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['poUploads', requestId] }),
    onError: () => setErr(t.manualQuote.uploadFailed),
  });
  const otherDeleteMut = useMutation({
    mutationFn: (poId: string) => comparisonApi.deletePo(requestId, poId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['poUploads', requestId] }),
    onError: () => setErr(t.manualQuote.deleteFailed),
  });

  function onPick(kind: 'QUOTATION' | 'INVOICE') {
    return async (e: ChangeEvent<HTMLInputElement>) => {
      setErr(null);
      const files = Array.from(e.target.files ?? []);
      e.target.value = '';
      for (const file of files) await uploadMut.mutateAsync({ kind, file }).catch(() => undefined);
    };
  }
  async function onPickOther(e: ChangeEvent<HTMLInputElement>) {
    setErr(null);
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    for (const file of files) await otherUploadMut.mutateAsync(file).catch(() => undefined);
  }

  async function handlePreview() {
    setPreviewing(true);
    try { await downloadPdf(comparisonApi.paymentPackageUrl(requestId), `liyekunthah-${requestId}.pdf`); }
    finally { setPreviewing(false); }
  }

  const heading = (
    <div className="section-heading">
      <h2 className="status-label text-muted-foreground">{t.manualQuote.heading}</h2>
    </div>
  );

  if (isLoading || !state) {
    return <div className="space-y-2">{heading}<p className="text-sm text-muted-foreground">{t.manualQuote.loading}</p></div>;
  }

  const award = state.award;
  const amountNum = Number(amount);
  const canAward = canAct && vendorId !== '' && Number.isFinite(amountNum) && amountNum > 0 && !awardMut.isPending;
  const vendors = (vendorData?.vendors ?? []).filter((v) => v.isActive || v.id === vendorId);

  return (
    <div className="space-y-4">
      {heading}

      {/* ── Before award: upload quotations + invoice, pick vendor + amount ── */}
      {!award && (
        <div className="space-y-3">
          <DocBox
            label={t.manualQuote.quotationsLabel}
            hint={t.manualQuote.quotationsHint}
            docs={state.quotations}
            uploading={uploadMut.isPending}
            onPick={onPick('QUOTATION')}
            onRemove={(id) => deleteMut.mutate(id)}
            removing={deleteMut.isPending}
            locked={!canAct}
          />
          <DocBox
            label={t.manualQuote.invoiceLabel}
            hint={t.manualQuote.invoiceHint}
            docs={state.invoice ? [state.invoice] : []}
            uploading={uploadMut.isPending}
            onPick={onPick('INVOICE')}
            onRemove={(id) => deleteMut.mutate(id)}
            removing={deleteMut.isPending}
            locked={!canAct}
          />

          {canAct && (
            <div className="card-paper space-y-3 p-4">
              <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                <label className="block">
                  <span className="status-label mb-1 block text-muted-foreground">{t.manualQuote.vendorLabel}</span>
                  <select
                    value={vendorId}
                    onChange={(e) => setVendorId(e.target.value)}
                    className="input w-full"
                  >
                    <option value="">{t.manualQuote.vendorPlaceholder}</option>
                    {vendors.map((v) => (
                      <option key={v.id} value={v.id}>{v.name}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="status-label mb-1 block text-muted-foreground">{t.manualQuote.amountLabel}</span>
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={t.manualQuote.amountPlaceholder}
                    className="input num w-full sm:w-40"
                    dir="ltr"
                  />
                </label>
              </div>
              <button
                type="button"
                onClick={() => { setErr(null); awardMut.mutate(); }}
                disabled={!canAward}
                className="btn-primary btn-sm"
              >
                {awardMut.isPending ? t.manualQuote.awarding : t.manualQuote.awardBtn}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── After award: banner + other docs + send for payment ── */}
      {award && (
        <section className="card-paper overflow-hidden border-s-4 border-[hsl(var(--success))]">
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 bg-[hsl(var(--success)/0.06)] px-4 py-3.5">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-[hsl(var(--success))] text-[hsl(var(--success))]" aria-hidden="true">
                <Icon name="check" size={20} />
              </span>
              <div className="leading-tight">
                <div className="status-label text-muted-foreground">{t.manualQuote.awardedTo}</div>
                <div className="text-lg font-semibold">{award.vendorName}</div>
              </div>
            </div>
            <div className="text-end leading-tight">
              <div className="status-label text-muted-foreground">{t.manualQuote.amountRow}</div>
              <div className="num tabular text-xl font-semibold text-[hsl(var(--success))]" dir="ltr">{fmtMvr(award.amount)}</div>
            </div>
          </div>

          <div className="space-y-4 border-t border-border p-4">
            {/* Status badge */}
            <div className="flex flex-wrap items-center gap-2">
              {award.paid ? (
                <span className="pill pill-success">{t.manualQuote.paidBadge}</span>
              ) : award.sentToFinanceAt ? (
                <span className="pill pill-pending">{t.manualQuote.sentToFinanceBadge}</span>
              ) : null}
              {canAct && (
                <button type="button" onClick={() => void handlePreview()} disabled={previewing} className="btn-secondary btn-sm gap-1">
                  <Icon name="visibility" size={14} />
                  {previewing ? t.manualQuote.previewLoading : t.manualQuote.previewPackage}
                </button>
              )}
            </div>

            {/* Other docs + send-for-payment (only while not yet sent) */}
            {otherEnabled && canAct && (
              <>
                <DocBox
                  label={t.manualQuote.otherDocsLabel}
                  hint={t.manualQuote.otherDocsHint}
                  docs={others}
                  uploading={otherUploadMut.isPending}
                  onPick={onPickOther}
                  onRemove={(id) => otherDeleteMut.mutate(id)}
                  removing={otherDeleteMut.isPending}
                />
                <button
                  type="button"
                  onClick={() => { setErr(null); sendMut.mutate(); }}
                  disabled={sendMut.isPending}
                  className="btn-primary btn-sm"
                >
                  {sendMut.isPending ? t.manualQuote.sending : t.manualQuote.sendForPayment}
                </button>
              </>
            )}
          </div>
        </section>
      )}

      {err && <p className="text-sm text-destructive">{err}</p>}
    </div>
  );
}
